import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { mutate } from "swr";

import { setDefaultSetting, setIsLoading } from "../store";
import { clearMultiCustomSelectionData } from "@components/Common/MultiCustomSelect/store";
import { clearData } from "@components/Common/MultiCustomCreatableSelection/store";
import { searchPosInArrayObject } from "@helpers/general";
import { logger } from "@helpers/logger";
import { editPayment } from "./helper";

export function useSyncDefaultSettingPayment(data: any, defaultSetting: any) {
    useEffect(() => {
        if (data.data === undefined) return;
        setDefaultSetting({
            ...defaultSetting,
            paymentMethod: { data: [...data.data] },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);
}

export function usePaymentModified(
    isPaymentModified: any,
    skipFirstEffect: boolean,
    setSkipFirstEffect: (val: boolean) => void,
    franchiseID: string,
    paymentMethod: any,
    setIsAddPaymentClicked: (val: boolean) => void,
    setDataIsUpdated: Function,
    dataIsUpdated: boolean | number,
    fetchGlobal: <T>(url: string, options?: RequestInit) => Promise<T>
) {
    useEffect(() => {
        if (skipFirstEffect) {
            setIsLoading(true);
            editPayment(
                franchiseID,
                paymentMethod,
                setIsAddPaymentClicked,
                setDataIsUpdated,
                dataIsUpdated,
                fetchGlobal
            );
        } else setSkipFirstEffect(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPaymentModified]);
}

export function useInitPaymentForm(
    paymentMethod: any,
    i18nextLng: string,
    defaultSetting: any,
    setTitle: (val: string) => void,
    setTagsData: (val: any) => void,
    setChoiceMode: (val: string) => void,
    setDisplay: (val: string) => void,
    setSalesSupport: (val: any) => void,
    setCountryData: (val: any) => void,
    setAdvancedDisplayName: (val: any) => void,
    setSalesSupportData: (val: any) => void
) {
    const { t } = useTranslation();

    useEffect(() => {
        clearMultiCustomSelectionData();
        clearData();
        setTitle(t("Modify a payment method"));
        setTagsData(paymentMethod.Tag.split(","));
        setChoiceMode(paymentMethod[t("Type of payment method")]);
        setDisplay(paymentMethod.advancedDisplayName.languages[i18nextLng]);
        setSalesSupport(paymentMethod[t("Sales support")].split(","));
        setCountryData(paymentMethod["Country"].labels);
        setAdvancedDisplayName(paymentMethod.advancedDisplayName);
        setSalesSupportData(
            defaultSetting.saleSupport.data
                .filter((el: any) => el.status === true)
                .map((elt: any) => elt.support_de_vente)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentMethod, t, i18nextLng, defaultSetting.saleSupport]);
}

export function useSyncDisplayOnChoiceMode(
    choiceMode: string,
    paymentMethod: any,
    setDisplay: (val: string) => void,
    setInputDisplay: (val: boolean) => void
) {
    useEffect(() => {
        if (paymentMethod.id === undefined) {
            setDisplay(choiceMode);
            setInputDisplay(false);
        }
        // eslint-disable-next-line
    }, [choiceMode]);
}

export function useFetchPaysPayment(
    fetchGlobal: <T>(url: string, options?: RequestInit) => Promise<T>,
    i18nextLng: string,
    oneShop: boolean,
    shopPays: any,
    setCountryList: (val: any) => void
) {
    useEffect(() => {
        let cancelled = false;
        const apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/pays`;

        (async () => {
            try {
                const data: any = await fetchGlobal<any>(apiUrl, {
                    headers: {
                        "Content-Type": "application/json",
                        "Accept-Language": i18nextLng,
                    },
                    method: "GET",
                });

                if (cancelled) return;
                if (data.error) throw Error("Error!");

                const listOfCountry = data.data.map((element: any) => ({
                    label: element["Designation"],
                    value: element.Id,
                    frDesignation: element.frenchDesignation,
                }));

                if (oneShop) {
                    const shopPaysItems =
                        listOfCountry[
                            searchPosInArrayObject(
                                listOfCountry,
                                "label",
                                shopPays
                            )
                        ];
                    if (shopPaysItems !== undefined) {
                        setCountryList([shopPaysItems.label]);
                    }
                } else {
                    setCountryList(listOfCountry);
                }

                mutate(apiUrl, data);
            } catch (e: any) {
                logger.error(
                    "Failed to load /settings/pays (PaymentMethod)",
                    e
                );
            }
        })();

        return () => {
            cancelled = true;
        };
        // fetchGlobal et setCountryList sont volontairement omis : refs instables (cf. useFetchGlobal).
        // Le refetch ne doit dépendre que de la langue et du périmètre boutique.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18nextLng, oneShop, shopPays]);
}

export function useResetInputDisplay(
    advancedDisplayName: any,
    i18nextLng: string,
    setInputDisplay: (val: boolean) => void
) {
    useEffect(() => {
        if (advancedDisplayName.languages[i18nextLng] !== "") {
            setInputDisplay(false);
        }
        // eslint-disable-next-line
    }, [advancedDisplayName, i18nextLng]);
}

export function useSyncIsSupported(
    defaultSetting: any,
    supportVente: any[],
    setIsSupported: (val: boolean) => void
) {
    useEffect(() => {
        const filteredSaleSupport: any = (
            defaultSetting?.saleSupport?.data ?? []
        ).filter((item: any) => item.isActive === true);
        const supportedSaleModes = filteredSaleSupport.map(
            (support: any) => support.support_de_vente
        );
        const supportVenteArr = supportVente || [];
        const isSupported = supportVenteArr.some((mode: any) =>
            supportedSaleModes.includes(mode)
        );
        setIsSupported(isSupported);
    }, [defaultSetting, supportVente, setIsSupported]);
}
